import { Injectable, inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import Keycloak from 'keycloak-js';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { StateService } from '../../shared/services/state.service';
import { UserService } from '../../shared/services/user.service';

export const sessionResolver: ResolveFn<void> = () => inject(SessionBootstrapService).ensure();

/**
 * Sessão + empresa ativa, uma vez por carga do app. O guard das rotas (que roda ANTES do resolver)
 * também chama: ele precisa da empresa ativa certa para buscar a permissão efetiva do grupo dela.
 * Memoizado só depois de autenticado, para quem ainda não logou não ficar sem sessão.
 */
@Injectable({ providedIn: 'root' })
export class SessionBootstrapService {
  private readonly keycloak = inject(Keycloak);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly stateService = inject(StateService);
  private readonly userService = inject(UserService);

  private pending: Promise<void> | null = null;

  public ensure(): Promise<void> {
    if (!this.keycloak.authenticated) return Promise.resolve();
    return (this.pending ??= bootstrap(this.keycloak, this.localStorageService, this.stateService, this.userService));
  }
}

async function bootstrap(
  keycloak: Keycloak,
  localStorageService: LocalStorageService,
  stateService: StateService,
  userService: UserService,
): Promise<void> {

  // A sessão persistida pertence a UM usuário (sub do token). Trocar de conta sem passar pelo
  // logout do app — handoff do Workspace com outro usuário, sessão KC encerrada/expirada fora daqui
  // e login com outra conta — deixava nome, e-mail e EMPRESA ATIVA do usuário anterior no storage:
  // o novo usuário mandava X-Company-Id de empresa que não é dele e levava 403 em tudo até
  // "logar e deslogar várias vezes". Sessão de outro sub (ou sem sub, gravada antes desta regra)
  // é descartada junto com o contexto de empresa.
  const subject = keycloak.subject;
  const existing = localStorageService.getSession();
  const staleSession = !existing || (!!subject && existing.sub !== subject);

  if (staleSession) {
    stateService.limparContexto();

    // Claims do próprio token (scope profile/email) evitam um roundtrip ao Keycloak no primeiro
    // load; loadUserProfile fica só como fallback de token sem esses claims.
    const claims = (keycloak.idTokenParsed ?? keycloak.tokenParsed) as Record<string, string> | undefined;

    if (claims?.['given_name'] || claims?.['preferred_username'] || claims?.['email']) {
      localStorageService.registerSession({
        name: claims['given_name'] || claims['preferred_username'] || '',
        email: claims['email'] ?? '',
        sub: subject,
      });
    } else {
      const profile = await keycloak.loadUserProfile();
      localStorageService.registerSession({
        name: profile.firstName || profile.username || '',
        email: profile.email ?? '',
        sub: subject,
      });
    }
  }

  // Garante empresa ativa ANTES de qualquer página tenant-scoped disparar requests. Sem isso, o
  // request sai sem X-Company-Id (base.service só envia o header quando há empresa ativa) e o
  // backend rejeita com 400 — ou, no comportamento antigo, servia dados sob o contexto errado.
  // Esta é a raiz do vazamento cross-empresa reportado.
  if (!stateService.contextAtual) {
    try {
      const companies = await firstValueFrom(userService.GetUserCompanies());

      if (companies && companies.length > 0) {
        const persisted = localStorageService.getSession()?.tenant;
        // Do objeto da LISTA, não do persistido: a identidade do grupo (cor, logo, nome) pode ter
        // mudado desde a última visita, e a lista é a versão fresca.
        const valid = persisted && companies.find(c => c.id === persisted.id);

        stateService.setTenant(valid ?? companies[0]);
      }
    } catch {
      // Falhar ao listar empresas não deve travar a navegação. As páginas tenant-scoped
      // simplesmente não carregarão dados até haver empresa ativa.
    }
  }
}
